// constants/theme.js
// Updated: 2026-07-20 — reduced to three VALIDATED schemes; user-authored
// themes removed.
// Project: Giving Words to Voices
// Version: 2.0.0
//
// Three schemes, each defined for dark and light: Archive (warm, scholarly),
// Studio (cool, editing-suite), Ledger (neutral, structural). Every textSub
// value is checked to pass WCAG AA (>=4.5:1) on every surface — the token that
// used to fail across most schemes. Each scheme also carries REAL semantic
// status colours (success/warning/danger/info) instead of the old aliasing of
// success===warning===accent.
//
// User-authored themes were dropped by decision (2026-07-20): the custom editor
// let anyone pick colours that fail contrast, and it was why light mode
// inherited a dark palette. Schemes are now closed and validated.

export const COLOR_SCHEMES = {
  archive: {
    label: 'Archive',
    swatchDark: '#2A251E', swatchLight: '#F4F1EA',
    dark: {
      bg: '#1A1712', sidebar: '#221E18', card: '#2A251E', cardDone: '#1E1A14', text: '#EDE6D8',
      textSub: '#AEA48F', inputBg: '#151109', border: '#3A342A', accent: '#DE7A63', highlight: '#DE7A63',
      highlightText: '#1A1712', playhead: '#DE7A63', timecode: '#7FD8AE', waveProgress: '#6E6252', audioReview: '#D2A140',
      success: '#6FA97F', warning: '#D2A140', danger: '#E0745A', info: '#8FB0E8',
    },
    light: {
      bg: '#F4F1EA', sidebar: '#FBF9F4', card: '#FFFFFF', cardDone: '#F0ECE2', text: '#211C14',
      textSub: '#5C5347', inputBg: '#FFFFFF', border: '#E2DCCF', accent: '#8C2F22', highlight: '#8C2F22',
      highlightText: '#FFFFFF', playhead: '#8C2F22', timecode: '#2F6B4A', waveProgress: '#B0A48F', audioReview: '#8A5A18',
      success: '#2E7D46', warning: '#8A5A12', danger: '#A5271C', info: '#215FB0',
    },
  },
  studio: {
    label: 'Studio',
    swatchDark: '#1B1F23', swatchLight: '#EDF0F2',
    dark: {
      bg: '#0F1214', sidebar: '#15181B', card: '#1B1F23', cardDone: '#141719', text: '#E8ECEF',
      textSub: '#9AA2AA', inputBg: '#0C0F11', border: '#2A2F34', accent: '#E0663A', highlight: '#E0663A',
      highlightText: '#0F1214', playhead: '#E0663A', timecode: '#5FD3A0', waveProgress: '#5A646E', audioReview: '#D2A140',
      success: '#6FD8A0', warning: '#E4B765', danger: '#E5646A', info: '#79B0F0',
    },
    light: {
      bg: '#EDF0F2', sidebar: '#F6F8F9', card: '#FFFFFF', cardDone: '#E6EBEE', text: '#14181B',
      textSub: '#526069', inputBg: '#FFFFFF', border: '#D4DBE0', accent: '#C24E28', highlight: '#C24E28',
      highlightText: '#FFFFFF', playhead: '#C24E28', timecode: '#1F6B52', waveProgress: '#A9B4BC', audioReview: '#8A5A18',
      success: '#2E7D46', warning: '#8A5A12', danger: '#A5271C', info: '#215FB0',
    },
  },
  ledger: {
    label: 'Ledger',
    swatchDark: '#1F1F1D', swatchLight: '#FBFBFA',
    dark: {
      bg: '#121211', sidebar: '#1A1A18', card: '#1F1F1D', cardDone: '#171715', text: '#EDEDEA',
      textSub: '#9C9C95', inputBg: '#0E0E0D', border: '#2E2E2B', accent: '#E8E8E4', highlight: '#E8E8E4',
      highlightText: '#121211', playhead: '#E8E8E4', timecode: '#6FA97F', waveProgress: '#57574F', audioReview: '#D2A140',
      success: '#6FA97F', warning: '#D2A140', danger: '#E0745A', info: '#8FB0E8',
    },
    light: {
      bg: '#FBFBFA', sidebar: '#FFFFFF', card: '#FFFFFF', cardDone: '#F1F1EF', text: '#141414',
      textSub: '#5B5B56', inputBg: '#FFFFFF', border: '#E4E3E0', accent: '#1A1A1A', highlight: '#1A1A1A',
      highlightText: '#FFFFFF', playhead: '#1A1A1A', timecode: '#1E4D30', waveProgress: '#C9C8C4', audioReview: '#6B4A0C',
      success: '#2E7D46', warning: '#7A5410', danger: '#A5271C', info: '#215FB0',
    },
  },
};

export const FONT_OPTIONS = [
  { key: 'system',      label: 'System Default',  value: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  { key: 'inter',       label: 'Inter',            value: "'Inter', sans-serif" },
  { key: 'ibmplex',     label: 'IBM Plex Sans',    value: "'IBM Plex Sans', sans-serif" },
  { key: 'jetbrains',   label: 'JetBrains Mono',   value: "'JetBrains Mono', monospace" },
  { key: 'roboto',      label: 'Roboto',           value: "'Roboto', sans-serif" },
  { key: 'sourceserif', label: 'Source Serif 4',   value: "'Source Serif 4', serif" },
];

export const GOOGLE_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=IBM+Plex+Sans:wght@400;600;700&family=JetBrains+Mono:wght@400;700&family=Roboto:wght@400;700&family=Source+Serif+4:wght@400;700&display=swap';

// Old scheme keys → nearest current scheme, so a saved preference still resolves
// after the reduction from five schemes to three.
export const SCHEME_MIGRATION = {
  monochrome: 'ledger',
  classic:    'studio',
  slate:      'studio',
  warm:       'archive',
  forest:     'archive',
};

export function resolveScheme(key) {
  if (COLOR_SCHEMES[key]) return key;
  return SCHEME_MIGRATION[key] || 'ledger';
}


// Text colour (near-black or white) that stays legible on an arbitrary coloured
// background. Picks whichever of the two contrasts MORE, rather than using a
// luminance threshold — a mid-tone accent (a warm orange at L~0.3, or Ledger's
// near-white accent) is mishandled by a fixed cutoff. Used for buttons whose
// background is a theme accent or a per-item colour, where a hardcoded white
// label went invisible on the lighter schemes.
export function contrastText(bg, dark = '#141210', light = '#ffffff') {
  const lum = (hex) => {
    const h = String(hex || '').replace('#', '');
    const c = h.length === 3 ? h.replace(/(.)/g, '$1$1') : h;
    if (c.length < 6) return 0;
    const ch = [0, 2, 4].map((i) => {
      const v = parseInt(c.slice(i, i + 2), 16) / 255;
      return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
  };
  const L = lum(bg);
  const cr = (a) => (Math.max(L, a) + 0.05) / (Math.min(L, a) + 0.05);
  return cr(lum(dark)) >= cr(lum(light)) ? dark : light;
}
